import { fieldSectionEligibility, type ConversationSection } from '../../lib/conversation-sections';
import React, { useCallback, useMemo, useState } from 'react';
import type { FormField } from '../../types';
import type { RespondentAnswer, RespondentCalendar, RespondentConversationState } from '../../lib/multimodal-contract';
import { projectPreviewField } from '../../lib/respondent-preview';
import { evaluateQuestionCondition, type ConditionAnswer } from '../../lib/question-conditions';
import { Modal } from '../ui/Modal';
import { ConversationThread } from '../conversation/ConversationThread';
import { validateDirectAnswer, type ControlAnswer } from '../conversation/useRespondentConversation';
import { resolveDateRuleBounds, formatQuestionAnswer, validateQuestionStructuredValue, type QuestionStructuredValue } from '../../lib/question-blocks';
import { SIGNING_CONSENT_STATEMENT, SIGNING_CONSENT_VERSION } from '../../lib/signing-contract';

/** A local preview of authored questions using the production respondent renderer. */
export interface QuestionPreviewProps { fields: readonly FormField[]; sections?: readonly ConversationSection[]; onClose: () => void }
const button = 'cursor-pointer rounded-lg px-3 py-2 text-sm text-foreground hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed';

/** The local simulator uses the same condition evaluator as the live engine. */
function previewPath(fields: readonly FormField[], answers: Readonly<Record<string, RespondentAnswer>>, sections: readonly ConversationSection[] = []) {
  const facts: Record<string, ConditionAnswer> = {};
  const visible = new Set<string>();
  const invalidDates = new Set<string>();
  const dateAnswers: Record<string, unknown> = {};
  for (const field of fields) {
    const eligible = (!field.informationFieldId || visible.has(field.informationFieldId))
      && fieldSectionEligibility(field.id, sections, facts) === true
      && evaluateQuestionCondition(field.showWhen, facts) === true;
    if (eligible) visible.add(field.id);
    const answer = answers[field.id];
    if (eligible && field.type === 'date_time' && answer?.status === 'answered') {
      if (validateQuestionStructuredValue(field, answer.structuredValue, { answers: dateAnswers }).valid) dateAnswers[field.id] = answer.structuredValue;
      else invalidDates.add(field.id);
    }
    facts[field.id] = { source: field, status: !eligible ? 'hidden' : !answer || invalidDates.has(field.id) ? 'pending' : answer.status === 'answered' ? 'answered' : 'unanswered',
      value: answer?.structuredValue ?? answer?.value,
      optionIds: answer?.optionIds ?? (field.type === 'image_choice' && Array.isArray(answer?.structuredValue) ? answer.structuredValue as string[] : undefined) };
  }
  return { visible, invalidDates, dateAnswers };
}

/** Illustrative local times let authors try the controls without accessing a calendar. */
function sampleCalendar(durationMinutes: number): RespondentCalendar {
  const start = new Date();
  start.setDate(start.getDate() + 1);
  start.setHours(9, 0, 0, 0);
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const format = new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' });
  const dates = Array.from({ length: 7 }, (_, index) => { const day = new Date(start); day.setDate(day.getDate() + index); return day; });
  const slots = dates.filter(day => day.getDay() !== 0 && day.getDay() !== 6).flatMap(day => [9, 10, 11, 13, 14, 15].map(hour => {
    const time = new Date(day); time.setHours(hour);
    return { id: `preview-${time.getTime()}`, startsAt: time.toISOString(), endsAt: new Date(time.getTime() + durationMinutes * 60_000).toISOString() };
  }));
  const firstDay = format.format(start);
  const lastDay = format.format(dates[6]);
  return { status: 'available', timezone, durationMinutes, slots, page: { startDay: firstDay, endDay: lastDay, firstDay, lastDay } };
}

/** Answers exist only while this preview is open; live services are never called. */
export function QuestionPreview({ fields, sections = [], onClose }: QuestionPreviewProps): React.ReactElement {
  const baseProjected = useMemo(() => fields.map(field => ({ ...projectPreviewField(field, fields),
    // Preview cannot persist bookings, and informational blocks collect no required answer.
    ...(field.type === 'calendar_booking' || field.type === 'information' ? { required: false } : {}),
    ...(field.type === 'calendar_booking' ? { calendar: sampleCalendar(field.durationMinutes) } : {}),
  })), [fields]);
  const [index, setIndex] = useState(0);
  const [visitedIds, setVisitedIds] = useState<Set<string>>(() => new Set());
  const [answers, setAnswers] = useState<Record<string, RespondentAnswer>>({});
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [editText, setEditText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingSnapshot, setEditingSnapshot] = useState<{ selection?: string[]; structuredValue?: QuestionStructuredValue } | null>(null);
  const [selections, setSelections] = useState<Record<string, string[]>>({});
  const [structuredValues, setStructuredValues] = useState<Record<string, QuestionStructuredValue | undefined>>({});
  const [submitted, setSubmitted] = useState(false);
  const [revision, setRevision] = useState(0);
  const { visible: visibleIds, invalidDates, dateAnswers } = previewPath(fields, answers, sections);
  const dateContext = { answers: dateAnswers };
  const projected = baseProjected.filter(candidate => visibleIds.has(candidate.fieldId)).map(candidate => candidate.dateTime?.rules
    ? { ...candidate, dateBounds: resolveDateRuleBounds(candidate.dateTime, dateContext) } : candidate);
  const activeField = projected[index] ?? null;
  const editingField = projected.find(field => field.fieldId === editingId) ?? null;
  const field = editingField ?? activeField;
  const savedValue = field ? answers[field.fieldId]?.value : undefined;
  const questionText = field ? drafts[field.fieldId] ?? (typeof savedValue === 'string' || typeof savedValue === 'number' ? String(savedValue) : '') : '';
  const text = editingField ? editText ?? questionText : questionText;
  /** Keep each private draft when navigating or editing an accepted answer. */
  const setText = (value: string): void => {
    if (editingField) setEditText(value);
    else if (field) setDrafts(previous => ({ ...previous, [field.fieldId]: value }));
  };
  const accepted = Object.values(answers).filter(answer => visibleIds.has(answer.fieldId)).map(answer => invalidDates.has(answer.fieldId) ? { ...answer, status: 'conflict' as const } : answer);
  const missing = projected.filter(candidate => invalidDates.has(candidate.fieldId) || candidate.required && answers[candidate.fieldId]?.status !== 'answered');
  const atEnd = index >= projected.length;
  const needsSignatureConfirmation = accepted.some(answer => answer.field?.type === 'signature' && answer.status === 'answered');
  const state: RespondentConversationState = {
    version: 1, responseId: 'local-preview', revision,
    status: atEnd ? missing.length ? 'needs_attention' : 'ready' : 'collecting', entry: 'text',
    canStartCall: false, canSubmit: atEnd && missing.length === 0, activeField,
    progress: { resolved: accepted.length, total: projected.length, answered: accepted.filter(answer => answer.status === 'answered').length,
      skipped: accepted.filter(answer => answer.status === 'skipped').length },
    hiddenFieldIds: fields.filter(candidate => !visibleIds.has(candidate.id)).map(candidate => candidate.id),
    messages: [], answers: projected.filter(candidate => atEnd || candidate.fieldId === activeField?.fieldId || visitedIds.has(candidate.fieldId) || answers[candidate.fieldId])
      .map(candidate => ({ ...(answers[candidate.fieldId] ?? { fieldId: candidate.fieldId, status: 'missing' as const }), ...(invalidDates.has(candidate.fieldId) ? { status: 'conflict' as const } : {}), field: candidate })),
    ...(needsSignatureConfirmation && atEnd && missing.length === 0 ? { signing: { status: 'required', version: SIGNING_CONSENT_VERSION,
      statement: SIGNING_CONSENT_STATEMENT, responseRevision: revision } as const } : {}),
  };

  /** Move within the preview without altering the authored definition. */
  const navigate = (nextIndex: number): void => {
    if (activeField) setVisitedIds(previous => new Set([...previous, activeField.fieldId]));
    setIndex(nextIndex); setEditingId(null); setEditText(null); setEditingSnapshot(null); setError(null);
  };
  /** Record only the local displayed answer, then return to the active question. */
  const accept = (answer: Omit<RespondentAnswer, 'fieldId' | 'field'>): void => {
    if (!field) return;
    const nextAnswers = { ...answers, [field.fieldId]: { ...answer, fieldId: field.fieldId,
      ...(field.type !== 'information' ? { field } : {}) } };
    const { visible: nextPath } = previewPath(fields, nextAnswers, sections);
    for (const id of Object.keys(nextAnswers)) if (!nextPath.has(id)) delete nextAnswers[id];
    setAnswers(nextAnswers);
    if ((fields.some(candidate => candidate.showWhen) || sections.some(section => section.showWhen))) {
      const nextFields = fields.filter(candidate => nextPath.has(candidate.id));
      const nextIndex = nextFields.findIndex(candidate => !nextAnswers[candidate.id]);
      setIndex(nextIndex < 0 ? nextFields.length : nextIndex);
      setSelections(previous => Object.fromEntries(Object.entries(previous).filter(([id]) => nextPath.has(id))));
      setStructuredValues(previous => Object.fromEntries(Object.entries(previous).filter(([id]) => nextPath.has(id))));
      setDrafts(previous => Object.fromEntries(Object.entries(previous).filter(([id]) => nextPath.has(id))));
    }
    setRevision(previous => previous + 1);
    if (!editingField && !(fields.some(candidate => candidate.showWhen) || sections.some(section => section.showWhen))) setIndex(current => current + 1);
    setDrafts(previous => { const next = { ...previous }; delete next[field.fieldId]; return next; });
    setEditingId(null); setEditText(null); setEditingSnapshot(null); setError(null);
  };
  /** Share live validation for direct input without invoking semantic interpretation. */
  const send = (value = text): void => {
    if (!field) return;
    if (field.type === 'choice_single' || field.type === 'choice_multi' || field.rating) {
      setError('Choose an option in preview. Text interpretation runs in the live conversation.'); return;
    }
    if (field.type === 'file_upload') { setError('Use the example file in preview.'); return; }
    if (field.type === 'calendar_booking') { setError('Choose a sample time in preview.'); return; }
    const answer = field.type === 'number' ? value.trim() ? Number(value.trim()) : Number.NaN : value.trim();
    const validation = validateDirectAnswer(field, answer);
    if (validation) { setError(validation); return; }
    accept({ status: 'answered', value: answer, displayValue: String(answer) });
  };
  /** Keep canonical option identities so review edits restore selected controls. */
  const answerControl = (answer: ControlAnswer): void => {
    if (!field) return;
    if (answer.structuredValue !== undefined) {
      const authored = fields.find(candidate => candidate.id === field.fieldId);
      if (!authored) return;
      const validation = validateQuestionStructuredValue(authored, answer.structuredValue, dateContext);
      if (!validation.valid) { setError('Complete this answer before continuing.'); return; }
      const displayValue = formatQuestionAnswer(authored, validation.value) ?? 'Answered';
      accept({ status: 'answered', value: displayValue, structuredValue: validation.value, displayValue });
      return;
    }
    if (answer.attachmentIds) { accept({ status: 'answered', value: answer.attachmentIds, displayValue: 'Example file' }); return; }
    if (answer.calendar) {
      if (answer.calendar.action !== 'book') return;
      const slotId = answer.calendar.slotId;
      const slot = field.calendar?.slots?.find(candidate => candidate.id === slotId);
      if (field.type !== 'calendar_booking' || !slot) { setError('Choose a sample time in preview.'); return; }
      const displayValue = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short', timeZone: field.calendar?.timezone }).format(new Date(slot.startsAt));
      accept({ status: 'answered', value: slot.startsAt, displayValue });
      return;
    }
    if (answer.optionIds) {
      const otherSelected = Boolean(field.otherOptionId && answer.optionIds.includes(field.otherOptionId));
      const otherText = answer.otherText?.trim();
      if (otherSelected && !otherText) { setError('Specify Other before continuing.'); return; }
      const displayValue = answer.optionIds.map(id => {
        const label = field.options?.find(option => option.id === id)?.label ?? '';
        return id === field.otherOptionId && otherText ? `${label}: ${otherText}` : label;
      }).join(', ');
      accept({ status: 'answered', value: answer.optionIds, optionIds: answer.optionIds, displayValue, ...(otherSelected ? { otherText } : {}) });
    } else if (answer.value !== undefined) accept({ status: 'answered', value: answer.value, displayValue: String(answer.value) });
  };
  /** Prefill accepted input without remapping option labels or machine values. */
  const edit = (fieldId: string): void => {
    const answer = answers[fieldId];
    setEditingId(fieldId); setEditText(null); setError(null);
    setEditingSnapshot({ selection: selections[fieldId], structuredValue: structuredValues[fieldId] });
    if (answer) {
      setSelections(previous => ({ ...previous, [fieldId]: answer.optionIds ?? [] }));
      setStructuredValues(previous => ({ ...previous, [fieldId]: answer.structuredValue }));
    }
  };
  /** Cancel discards only the correction; the current question keeps its draft. */
  const cancelEdit = (): void => {
    if (editingId) {
      setSelections(previous => { const next = { ...previous }; if (editingSnapshot?.selection) next[editingId] = editingSnapshot.selection; else delete next[editingId]; return next; });
      setStructuredValues(previous => ({ ...previous, [editingId]: editingSnapshot?.structuredValue }));
    }
    setEditingId(null); setEditText(null); setEditingSnapshot(null); setError(null);
  };
  const presented = useCallback(async () => true, []);

  return <Modal open fullScreenOnMobile onClose={onClose} title="Question preview" maxWidth="max-w-2xl">
    <div className="flex h-[70dvh] min-h-0 flex-col max-sm:h-auto max-sm:flex-1">
      <div className="flex shrink-0 items-center justify-between gap-4 border-b border-border pb-3 max-sm:px-4">
        <p className="text-sm text-muted-foreground">Answers stay in this preview.</p>
        <button type="button" className={button} onClick={onClose}>Close</button>
      </div>
      {submitted ? <div className="flex-1 space-y-4 py-8 max-sm:px-4">
        <h2 className="text-xl font-semibold">Preview complete</h2>
        <button type="button" className={button} onClick={() => { setAnswers({}); setSelections({}); setStructuredValues({}); setDrafts({}); setVisitedIds(new Set()); setSubmitted(false); navigate(0); }}>Restart</button>
      </div> : <>
        {(field?.type === 'free_form_explore' || field?.type === 'calendar_booking') && <p className="px-4 pt-4 text-sm text-muted-foreground">
          {field.type === 'calendar_booking' ? 'Sample times. Bookings run in the live conversation.' : 'AI follow-ups run in the live conversation.'}
        </p>}
        <div className="min-h-0 flex-1 overflow-hidden rounded-xl bg-background max-sm:rounded-none">
          <ConversationThread allowAttachments={false} followActiveQuestion chapterOrder={projected.map(candidate => candidate.fieldId)}
            state={state} draft={{ text, fieldId: field?.fieldId ?? null, label: field?.label ?? '' }}
            pending={false} error={error} selections={selections} onSelectionsChange={setSelections}
            structuredValues={structuredValues} onStructuredValueChange={(fieldId, value) => setStructuredValues(previous => ({ ...previous, [fieldId]: value }))}
            editingField={editingField} onEditAnswer={edit} onCancelEdit={cancelEdit}
            onDraftChange={setText} onSend={send} onAnswer={answerControl} onPresented={presented}
            onSkip={() => accept({ status: 'skipped' })} onContinueInformation={() => accept({ status: 'answered' })}
            onSubmit={() => setSubmitted(true)} />
        </div>
        {!editingField ? <div className="flex shrink-0 items-center justify-between gap-3 border-t border-border pt-3 max-sm:px-4 max-sm:pb-[max(env(safe-area-inset-bottom),1rem)]">
          <label className="flex min-w-0 flex-1 items-center gap-3 text-sm text-muted-foreground">
            <span className="shrink-0">Preview question</span>
            <select value={Math.min(index, projected.length)} onChange={event => navigate(Number(event.target.value))}
              className="min-h-11 min-w-0 flex-1 truncate rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-1 focus-visible:outline-ring">
              {projected.map((candidate, questionIndex) => <option key={candidate.fieldId} value={questionIndex}>{questionIndex + 1}. {candidate.label}</option>)}
              <option value={projected.length}>Completion</option>
            </select>
          </label>
        </div> : null}
      </>}
    </div>
  </Modal>;
}
